import { useState } from 'react';
import { ExternalLink, Mail } from 'lucide-react';

import QuoteBreakdown, { type QuoteBreakdownQuote } from '@/components/QuoteBreakdown';
import { Button } from '@/components/ui/button';
import { formatInstant } from '@/lib/date';

/**
 * Every quote this vendor has sent — one at a time.
 *
 * A quote is not a field that gets overwritten. A photographer sends a
 * proposal in August, the couple adds eighty guests, and October brings a
 * second PDF with different tiers. Both are real, the planner needs to compare
 * them, and only one is the price now.
 *
 * But "both are real" is not an argument for putting both on screen at once.
 * Stacked, two three-tier proposals are six package cards in a column with a
 * rule somewhere in the middle, and the reader's first question — which of
 * these am I looking at — gets asked again at every scroll position. So the
 * quotes become a row of dates to choose from with one quote below: the
 * comparison is a click rather than a scroll, and at any moment exactly one
 * price is on screen with its date above it.
 *
 * The picker disappears when there is one quote, which is most inquiries. A
 * control offering a choice of one is furniture.
 */

/**
 * Neither `currency` nor `source` is on a history entry, and both are needed
 * to render one. The currency belongs to the inquiry, not to any single
 * reading — `inquiries.quoteCurrency` is what every extraction on the thread
 * is stamped with — and the source is fixed: a row in `quotes` exists only
 * because the extractor made it. A planner's own typed figure has no tree and
 * never reaches this list.
 */
export interface QuoteHistoryEntry extends Omit<QuoteBreakdownQuote, 'currency' | 'source'> {
  id: string;
  sourceKind: string;
  sourceMessageId: string | null;
  messageSentAt: string;
  rejectedAt: string | null;
  isCurrent: boolean;
}

/** What the planner needs to reach the original, per source message. */
export interface SourceMessage {
  /** Position in the message's own attachment list, which is what the
   *  streaming route addresses. Null when the message carried none. */
  attachmentIndex: number | null;
  attachmentName: string | null;
  /** Where the bytes come back from, built by the page that knows the
   *  inquiry id. Null when there is nothing to open. */
  attachmentHref: string | null;
}

/**
 * Where a quote stands — and deliberately only when there is something to say.
 *
 * "Superseded" used to be the third case, and it was noise: with the current
 * quote marked on the picker at all times, every chip without that mark is
 * superseded by definition, and labelling both sides of a binary twice makes
 * the reader check which one they are on. "Struck out" survives because it is
 * not the complement of anything — it is the planner's own veto, and nothing
 * else on the panel says it happened.
 */
function standing(entry: QuoteHistoryEntry): { label: string; className: string } | null {
  if (entry.rejectedAt) {
    return { label: 'Struck out', className: 'bg-destructive/10 text-destructive' };
  }
  if (entry.isCurrent) {
    return {
      label: 'Current',
      className: 'bg-[var(--color-primary-container)] text-[var(--color-accent-foreground)]',
    };
  }
  return null;
}

export default function QuoteHistory({
  entries,
  currency,
  sources,
  onOpenMessage,
}: {
  /** Newest first, as the server sorts them. */
  entries: QuoteHistoryEntry[];
  currency: string;
  /** Keyed by `sourceMessageId`. Absent for a quote whose message is gone —
   *  disconnecting Gmail deletes the mirror and a quote outlives its source. */
  sources: Record<string, SourceMessage>;
  onOpenMessage: (messageId: string) => void;
}) {
  /**
   * Opens on the quote in force rather than on the newest.
   *
   * They are usually the same, and when they are not the difference is the
   * point: a planner who struck out the newest reading did it because that
   * reading was wrong, and landing them back on it every visit would be the
   * page arguing with them.
   */
  const [chosenId, setChosenId] = useState<string | null>(null);
  const shown =
    entries.find((e) => e.id === chosenId) ?? entries.find((e) => e.isCurrent) ?? entries[0];
  if (!shown) return null;

  const mark = standing(shown);
  const source = shown.sourceMessageId ? sources[shown.sourceMessageId] : undefined;

  return (
    <div className="space-y-3">
      {entries.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Oldest to newest, left to right. The server sorts newest first
              because that is the order a list wants; a row of dates is a
              timeline, and a timeline that runs backwards makes the reader
              translate every glance.

              Buttons with `aria-pressed` rather than a second tablist: this
              already sits inside the Quotes tab, and tabs within tabs read as
              two levels of the same thing. */}
          {[...entries].reverse().map((entry) => {
            const chosen = entry.id === shown.id;
            return (
              <button
                key={entry.id}
                type="button"
                aria-pressed={chosen}
                onClick={() => setChosenId(entry.id)}
                className={[
                  'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                  chosen
                    ? 'border-primary bg-[var(--color-primary-container)] text-[var(--color-accent-foreground)]'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-foreground',
                ].join(' ')}
              >
                <span className="font-medium tabular-nums">
                  {formatInstant(entry.messageSentAt, { month: 'short', day: 'numeric' })}
                </span>
                {/* Always, chosen or not. This mark is what makes the rest of
                    the row legible without a second label on every other chip:
                    one of these is the price now, and it says so wherever the
                    planner happens to be standing. */}
                {standing(entry) && (
                  <span
                    className={[
                      'rounded-full px-1.5 py-0.5 text-[0.7rem] font-medium',
                      chosen ? 'bg-white/50' : standing(entry)!.className,
                    ].join(' ')}
                  >
                    {standing(entry)!.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="label-caps flex items-center gap-2 text-xs text-muted-foreground">
          {formatInstant(shown.messageSentAt, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
          {/* Only the veto. "Current" is on the picker above and stays there;
              repeating it here would be the same fact twice in two inches. */}
          {shown.rejectedAt && mark && (
            <span
              className={`rounded-full px-2 py-0.5 text-[0.7rem] font-medium tracking-normal ${mark.className}`}
            >
              {mark.label}
            </span>
          )}
        </h3>

        {/* The way back to the vendor's own words. A figure a model read is a
            claim, and the planner is entitled to check it against the thing it
            was read from — which for an attachment means the document itself,
            not the note it came with. */}
        <div className="flex items-center gap-1">
          {source?.attachmentHref && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => window.open(source.attachmentHref!, '_blank', 'noopener')}
            >
              <ExternalLink className="size-3" />
              {source.attachmentName ?? 'Open the document'}
            </Button>
          )}
          {shown.sourceMessageId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onOpenMessage(shown.sourceMessageId!)}
            >
              <Mail className="size-3" />
              Open the message
            </Button>
          )}
        </div>
      </div>

      {/* Full contrast whatever this quote's standing.

          An earlier version dimmed a superseded quote to `opacity-70`, on the
          reasoning that a planner must not mistake it for what they can book
          today. Two things were wrong with that. The panel had just stopped
          labelling "Superseded" — the picker above says which quote is
          current — so the fade was a status signal with nothing left to name
          it, and it read as a rendering fault rather than as a state. And it
          put every muted line on the panel at 2.65:1 against the card, well
          under the 4.5:1 body text has to clear; the vendor's own inclusions
          were the least legible text on screen precisely when the planner was
          reading them to compare two offers.

          What tells the two apart is the picker, which names the current
          quote and keeps that mark on the chip that owns it. That is a label,
          which survives being read. */}
      <QuoteBreakdown quote={{ ...shown, currency, source: 'extracted' }} />
    </div>
  );
}
